import { useEffect, useMemo, memo, useCallback } from 'react';
import { useBlockLayout, usePagination, useResizeColumns, useSortBy, useTable } from 'react-table';
import { local } from '../../util/storage';
import { ReactComponent as SortArrowIcon } from '../../../img/icons/sortArrow.svg';
import classNames from 'classnames';
import ReactPaginate from 'react-paginate';
import { ReactComponent as ArrowIcon } from '../../../img/icons/arrow.svg';

const defaultPropGetter = () => ({})

const Grid = ({ className, columns, data, storageKey, defaultColumnWidth = 150, initialState = {}, onRowClick, onRowsChange, getCellProps = defaultPropGetter, getRowProps = defaultPropGetter, selectedRow, selectedCell, pagination }) => {
    const getGridSettings = () => {
        if (!storageKey) {
            return {};
        }

        const value = local.getItem(storageKey);

        if (!value) {
            return {};
        }

        return JSON.parse(value);
    }

    const setGridSettings = (newValue) => {
        if (!storageKey) {
            return;
        }

        const value = getGridSettings();

        const newSettings = {
            sortBy: [
                ...(newValue?.sortBy ? newValue.sortBy : value?.sortBy ? value.sortBy : []),
            ],
            columnWidths: {
                ...(value?.columnWidths ? value.columnWidths : {}),
                ...(newValue?.columnWidths ? newValue.columnWidths : {}),
            }
        };

        local.setItem(storageKey, JSON.stringify(newSettings));
    }

    const getSortBySettings = () => {
        const settings = getGridSettings();
        return settings?.sortBy || null;
    };

    const getColumnWidthsSettings = () => {
        const settings = getGridSettings();
        return settings?.columnWidths || {};
    };

    const extensiveColumns = useMemo(() => {
        return columns.map((column) => {
            const storedValue = getColumnWidthsSettings();
            return {
                ...column,
                width: storedValue[column.accessor] || column.width || defaultColumnWidth,
            }
        });
    }, [columns]);

    const instance = useTable({
            autoResetPage: false,
            disableMultiSort: true,
            columns: extensiveColumns,
            data,
            initialState: {
                pageSize: 10,
                ...initialState,
                ...(getSortBySettings() ? { sortBy: getSortBySettings() } : null),
            },
            sortTypes: {
                numeric: (rowA, rowB, columnName, desc) => {
                    if (typeof rowA.values[columnName] !== 'number' && typeof rowB.values[columnName] !== 'number') {
                        return 0;
                    }

                    if (typeof rowA.values[columnName] !== 'number') {
                        return desc ? -1 : 1;
                    }

                    if (typeof rowB.values[columnName] !== 'number') {
                        return desc ? 1 : -1;
                    }

                    return rowA.values[columnName] - rowB.values[columnName];
                },
                alphanumeric: (rowA, rowB, columnName, desc) => {
                    if (!rowA.values[columnName] && !rowB.values[columnName]) {
                        return 0;
                    }

                    if (!rowA.values[columnName]) {
                        return desc ? -1 : 1;
                    }

                    if (!rowB.values[columnName]) {
                        return desc ? 1 : -1;
                    }
                    console.log({
                        columnName,
                        rowA,
                    })
                    return rowA.values[columnName].toLowerCase().localeCompare(rowB.values[columnName].toLowerCase());
                }
            }
        },
        useBlockLayout,
        useSortBy,
        useResizeColumns,
        usePagination,
    );

    const {
        getTableProps,
        getTableBodyProps,
        headerGroups,
        rows,
        page,
        pageCount,
        gotoPage,
        prepareRow,
        state: { sortBy, columnResizing, pageIndex }
    } = instance;

    const tableRows = pagination ? page : rows;
    const paginationActivated = pagination && pageCount > 1;

    const classes = classNames('grid', className, {
        'grid--pagination': paginationActivated,
    });

    useEffect(() => {
        gotoPage(0);
        setGridSettings({
            sortBy,
        });
    }, [sortBy]);

    useEffect(() => {
        if (!onRowsChange) {
            return;
        }
        onRowsChange(rows.map((row) => row.original));
    }, [rows]);

    useEffect(() => {
        if (!Object.entries(columnResizing.columnWidths).length) {
            return;
        }

        setGridSettings({
            columnWidths: columnResizing.columnWidths,
        });
    }, [columnResizing]);

    const handleRowClick = useCallback((row, selectedRow) => {
        if (!onRowClick) {
            return;
        }
        onRowClick(row, selectedRow);
    }, []);

    const handlePageClick = (page) => {
        gotoPage(page.selected);
    }

    return (
        <>
            <div {...getTableProps()} className={classes}>
                <div className="grid__head">
                    {headerGroups.map((headerGroup) => (
                        <div {...headerGroup.getHeaderGroupProps()}>
                            {headerGroup.headers.map((column) => {
                                return (
                                    <div
                                        className={`grid__head-cell ${column.isSorted ? 'grid__head-cell--sorted' : ''}`} {...column.getHeaderProps()}>
                                        <div {...column.getSortByToggleProps()}
                                             className="grid__label">{column.render('Header')}</div>
                                        {column.isSorted ? <SortArrowIcon
                                            className={`grid__sortIcon ${column.isSortedDesc ? 'grid__sortIcon--desc' : ''}`} /> : null}
                                        <div
                                            {...column.getResizerProps()}
                                            className={`grid__resizer ${
                                                column.isResizing ? 'grid__resizer--isResizing' : ''
                                            }`}
                                        />
                                    </div>
                                )
                            })}
                        </div>
                    ))}
                </div>
                <div className="grid__body" {...getTableBodyProps()}>
                    {tableRows.map((row) => {
                        prepareRow(row)
                        return (
                            <div
                                {...row.getRowProps(getRowProps(row, selectedRow))}
                                onClick={() => handleRowClick(row, selectedRow)}
                            >
                                {row.cells.map((cell) => {
                                    return (
                                        <div className="grid__cell"
                                             {...cell.getCellProps([
                                                 {
                                                     className: cell.column.className,
                                                     style: cell.column.style,
                                                 },
                                                 // getColumnProps(cell.column),
                                                 getCellProps(cell, selectedRow, selectedCell),
                                             ])}>
                                            {cell.render('Cell')}
                                        </div>
                                    )
                                })}
                            </div>
                        )
                    })}
                </div>
            </div>
            {paginationActivated ? (
                <div className="grid__footer">
                    <div className="grid__footerInner">
                        <ReactPaginate
                            className="pagination"
                            breakLabel="..."
                            nextLabel={<ArrowIcon />}
                            onPageChange={handlePageClick}
                            pageRangeDisplayed={3}
                            marginPagesDisplayed={1}
                            pageCount={pageCount}
                            previousLabel={<ArrowIcon />}
                            initialPage={pageIndex}
                        />
                    </div>
                </div>
            ) : null}
        </>
    )
}

const propsAreEqual = (prevProps, nextProps) => {
    return JSON.stringify(prevProps) === JSON.stringify(nextProps);
}

export default memo(Grid, propsAreEqual);
